import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  HomeIcon, 
  UserIcon,
  Bars3Icon,
  XMarkIcon,
  Cog6ToothIcon,
  ArrowUpOnSquareIcon,
  CameraIcon,
  CheckIcon,
  XCircleIcon,
  PencilIcon,
  MapPinIcon,
  GlobeAltIcon,
  BriefcaseIcon,
  AcademicCapIcon,
  CalendarIcon,
  ClockIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { AuthContext } from '../contexts/AuthContext';
import { ref, onValue } from 'firebase/database';
import { database } from '../config/firebase';
import AppHeader from '../components/AppHeader';
import AppFooter from '../components/AppFooter';
import defaultProfile from '../assets/default-profile.png';
import InviteNotifications from '../components/InviteNotifications';
import activityService, { UserActivity, ActivityStats } from '../utils/activityService';
import { formatActivityTime, getActivityIcon } from '../utils/dateUtils';

interface ProfileData {
  displayName: string;
  email: string;
  photoURL: string;
  phoneNumber: string;
  linkedinUrl: string;
  jobTitle: string;
  company: string;
  bio: string;
  location: string;
  website: string;
  skills: string;
}

interface ValidationErrors {
  displayName?: string;
  email?: string;
  phoneNumber?: string;
  linkedinUrl?: string;
  website?: string;
  photoURL?: string;
  jobTitle?: string;
  company?: string;
  bio?: string;
  location?: string;
  skills?: string;
}

const ProfilePage: React.FC = () => {
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [pendingInvites, setPendingInvites] = useState<any[]>([]);
  
  // Profile data state
  const [profileData, setProfileData] = useState<ProfileData>({
    displayName: '',
    email: '',
    photoURL: '',
    phoneNumber: '',
    linkedinUrl: '',
    jobTitle: '',
    company: '',
    bio: '',
    location: '',
    website: '',
    skills: ''
  });

  // UI state
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Real-time activity and stats data
  const [recentActivity, setRecentActivity] = useState<UserActivity[]>([]);
  const [activityStats, setActivityStats] = useState<ActivityStats>({
    teamsJoined: 0,
    myDesigns: 0,
    collaborations: 0
  });
  const [isLoadingActivities, setIsLoadingActivities] = useState(true);

  const user = authContext?.user;

  useEffect(() => {
    if (user) {
      setProfileData({
        displayName: user.displayName || '',
        email: user.email || '',
        photoURL: user.photoURL || '',
        phoneNumber: user.phoneNumber || '',
        linkedinUrl: user.linkedinUrl || '',
        jobTitle: user.jobTitle || '',
        company: user.company || '',
        bio: user.bio || '',
        location: user.location || '',
        website: user.website || '',
        skills: user.skills || ''
      });
    }
  }, [user]);

  // Fetch real-time activity and stats data
  useEffect(() => {
    if (!user?.uid) return;

    let unsubscribeActivities: (() => void) | undefined;
    let unsubscribeStats: (() => void) | undefined;

    const setupRealTimeData = async () => {
      setIsLoadingActivities(true);
      
      try {
        // Backfill activities for existing designs (one-time operation)
        await activityService.backfillDesignActivities(user.uid);
        
        // Set up activity listener with small delay for better UX
        setTimeout(async () => {
          unsubscribeActivities = await activityService.getUserActivities(user.uid, (activities) => {
            setRecentActivity(activities);
            setIsLoadingActivities(false);
          });
        }, 500);

        // Set up stats listener
        unsubscribeStats = await activityService.getUserStats(user.uid, (stats) => {
          setActivityStats(stats);
        });
      } catch (error) {
        console.error('Error setting up real-time data:', error);
        setIsLoadingActivities(false);
      }
    };

    setupRealTimeData();

    return () => {
      if (unsubscribeActivities) unsubscribeActivities();
      if (unsubscribeStats) unsubscribeStats();
    };
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.email) return;
    const invitesRef = ref(database, 'invites');
    const unsub = onValue(invitesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const invitesList = Object.entries(data)
          .map(([id, invite]: [string, any]) => ({ id, ...invite }))
          .filter(invite => invite.email === user.email && invite.status === 'pending');
        setPendingInvites(invitesList);
      } else {
        setPendingInvites([]);
      }
    });
    return () => unsub();
  }, [user?.email]);

  if (!authContext) {
    return <div className="min-h-screen flex items-center justify-center">Loading authentication context...</div>;
  }
  
  const { updateUserProfile, uploadProfileImage, logout, requireDisplayNameMsg, setRequireDisplayNameMsg } = authContext;

  // Validation functions
  const validateField = (name: keyof ProfileData, value: string): string | undefined => {
    switch (name) {
      case 'displayName':
        return value.trim().length === 0 ? 'Display name is required' : undefined;
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return !emailRegex.test(value) ? 'Please enter a valid email address' : undefined;
      case 'phoneNumber':
        if (value && !/^[+]?[1-9][\d]{0,15}$/.test(value.replace(/[\s\-()]/g, ''))) {
          return 'Please enter a valid phone number';
        }
        return undefined;
      case 'linkedinUrl':
        if (value && !value.includes('linkedin.com')) {
          return 'Please enter a valid LinkedIn URL';
        }
        return undefined;
      case 'website':
        if (value && !/^https?:\/\/.+/.test(value)) {
          return 'Please enter a valid website URL (include http:// or https://)';
        }
        return undefined;
      default:
        return undefined;
    }
  };

  const validateForm = (): boolean => {
    const errors: ValidationErrors = {};
    Object.keys(profileData).forEach(key => {
      const fieldName = key as keyof ProfileData;
      const error = validateField(fieldName, profileData[fieldName]);
      if (error) {
        errors[fieldName] = error;
      }
    });
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFieldChange = (field: keyof ProfileData, value: string) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
    setIsDirty(true);
    
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImageFile(file);
      setProfileData(prev => ({ ...prev, photoURL: URL.createObjectURL(file) }));
      setIsDirty(true);
    }
  };

  const handleSave = async () => {
    if (!user) return;
    
    if (!validateForm()) {
      setSaveMessage({ type: 'error', message: 'Please fix the validation errors before saving.' });
      return;
    }

    setIsLoading(true);
    try {
      let newPhotoURL = profileData.photoURL;
      if (imageFile) {
        newPhotoURL = await uploadProfileImage(imageFile, user.uid);
      }
      
      await updateUserProfile({
        ...profileData,
        photoURL: newPhotoURL,
      });
      
      setIsDirty(false);
      setIsEditing(false);
      setRequireDisplayNameMsg && setRequireDisplayNameMsg(null);
      setSaveMessage({ type: 'success', message: 'Profile updated successfully!' });
      
      // Clear success message after 3 seconds
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      setSaveMessage({ type: 'error', message: 'Failed to update profile. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (user) {
      setProfileData({
        displayName: user.displayName || '',
        email: user.email || '',
        photoURL: user.photoURL || '',
        phoneNumber: user.phoneNumber || '',
        linkedinUrl: user.linkedinUrl || '',
        jobTitle: user.jobTitle || '',
        company: user.company || '',
        bio: user.bio || '',
        location: user.location || '',
        website: user.website || '',
        skills: user.skills || ''
      });
    }
    setIsEditing(false);
    setIsDirty(false);
    setValidationErrors({});
    setImageFile(null);
  };

  if (!user) {
    return <div className="min-h-screen flex items-center justify-center">Loading user profile...</div>;
  }

  const renderField = (field: keyof ProfileData, label: string, type: string = 'text', placeholder?: string, icon?: React.ReactNode) => (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
        {icon}
        {label}
      </label>
      {type === 'textarea' ? (
        <textarea
          value={profileData[field]}
          onChange={(e) => handleFieldChange(field, e.target.value)}
          disabled={!isEditing}
          className={`w-full px-4 py-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-all ${
            validationErrors[field] 
              ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
              : 'border-gray-300'
          } ${!isEditing ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'}`}
          rows={4}
          placeholder={placeholder}
        />
      ) : (
        <input
          type={type}
          value={profileData[field]}
          onChange={(e) => handleFieldChange(field, e.target.value)}
          disabled={!isEditing}
          className={`w-full px-4 py-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-all ${
            validationErrors[field] 
              ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
              : 'border-gray-300'
          } ${!isEditing ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'}`}
          placeholder={placeholder}
        />
      )}
      {validationErrors[field] && (
        <p className="text-red-500 text-xs flex items-center gap-1">
          <XCircleIcon className="w-3 h-3" />
          {validationErrors[field]}
        </p>
      )}
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <AppHeader />

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="sm:hidden fixed inset-0 z-[1002] bg-black bg-opacity-50" onClick={() => setIsMobileMenuOpen(false)}>
          <div className="absolute right-0 top-0 h-full w-64 bg-white shadow-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex flex-col space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Menu</h3>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-2 rounded-lg hover:bg-gray-100"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
              <div className="flex flex-col space-y-4">
                <button
                  onClick={() => {
                    navigate('/dashboard');
                    setIsMobileMenuOpen(false);
                  }}
                  className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <HomeIcon className="w-5 h-5 text-gray-600" />
                  <span className="text-gray-700">Dashboard</span>
                </button>
                <button
                  onClick={() => {
                    navigate('/profile');
                    setIsMobileMenuOpen(false);
                  }}
                  className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100 transition-colors bg-blue-50"
                >
                  <Cog6ToothIcon className="w-5 h-5 text-blue-600" />
                  <span className="text-blue-700 font-medium">Profile</span>
                </button>
                <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100 transition-colors">
                  <InviteNotifications />
                  <span className="text-gray-700">Notifications</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Show display name required message if present */}
      {requireDisplayNameMsg && (
        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 text-center text-sm">
          {requireDisplayNameMsg}
        </div>
      )}

      {/* Success/Error Messages */}
      {saveMessage && (
        <div className={`fixed top-20 right-4 z-[1001] p-4 rounded-lg shadow-lg flex items-center gap-2 animate-fade-in ${
          saveMessage.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
        }`}>
          {saveMessage.type === 'success' ? (
            <CheckIcon className="w-5 h-5" />
          ) : (
            <XCircleIcon className="w-5 h-5" />
          )}
          <span>{saveMessage.message}</span>
          <button onClick={() => setSaveMessage(null)} className="ml-2 font-bold">&times;</button>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        <div className="max-w-6xl mx-auto">
          {/* Profile Header Card */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-8">
            {/* Cover Image */}
            <div className="h-48 bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700 relative">
              <div className="absolute inset-0 bg-black bg-opacity-20"></div>
            </div>
            
            {/* Profile Info */}
            <div className="relative px-6 pb-6">
              <div className="flex flex-col sm:flex-row sm:items-end gap-4 -mt-16">
                {/* Avatar */}
                <div className="relative">
                  <div className="w-32 h-32 rounded-full border-4 border-white shadow-lg overflow-hidden bg-gray-200">
                    <img
                      src={profileData.photoURL || defaultProfile}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  {isEditing && (
                    <label htmlFor="profile-upload" className="absolute bottom-2 right-2 bg-blue-600 rounded-full p-2 cursor-pointer hover:bg-blue-700 transition-colors shadow-lg">
                      <CameraIcon className="w-4 h-4 text-white" />
                      <input
                        id="profile-upload"
                        type="file"
                        className="hidden"
                        onChange={handleImageChange}
                        accept="image/*"
                      />
                    </label>
                  )}
                </div>
                
                {/* Name and Actions */}
                <div className="flex-1 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                  <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{profileData.displayName}</h1>
                    <p className="text-gray-600 text-sm sm:text-base">{profileData.email}</p>
                    {profileData.jobTitle && profileData.company && (
                      <p className="text-gray-500 text-sm mt-1">
                        {profileData.jobTitle} at {profileData.company}
                      </p>
                    )}
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    {!isEditing ? (
                      <button
                        onClick={() => setIsEditing(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                      >
                        <PencilIcon className="w-4 h-4" />
                        Edit Profile
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={handleCancel}
                          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSave}
                          disabled={isLoading || !isDirty}
                          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isLoading ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            <CheckIcon className="w-4 h-4" />
                          )}
                          {isLoading ? 'Saving...' : 'Save Changes'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Profile Form */}
            <div className="lg:col-span-2 space-y-8">
              {/* Contact Information */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
                  <UserIcon className="w-5 h-5 text-blue-600" />
                  Contact Information
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {renderField('displayName', 'Display Name', 'text', 'Enter your display name', <UserIcon className="w-4 h-4" />)}
                  {renderField('email', 'Email Address', 'email', 'Enter your email address')}
                  {renderField('phoneNumber', 'Phone Number', 'tel', '+1 (555) 123-4567', <MapPinIcon className="w-4 h-4" />)}
                  {renderField('location', 'Location', 'text', 'e.g., New York, NY', <MapPinIcon className="w-4 h-4" />)}
                </div>
              </div>

              {/* Professional Information */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
                  <BriefcaseIcon className="w-5 h-5 text-blue-600" />
                  Professional Information
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {renderField('jobTitle', 'Job Title', 'text', 'e.g., Senior Designer', <BriefcaseIcon className="w-4 h-4" />)}
                  {renderField('company', 'Company', 'text', 'e.g., Tech Company Inc.', <BriefcaseIcon className="w-4 h-4" />)}
                  {renderField('linkedinUrl', 'LinkedIn URL', 'url', 'https://www.linkedin.com/in/yourprofile')}
                  {renderField('website', 'Website', 'url', 'https://www.yourwebsite.com', <GlobeAltIcon className="w-4 h-4" />)}
                </div>
              </div>

              {/* Bio and Skills */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
                  <AcademicCapIcon className="w-5 h-5 text-blue-600" />
                  About & Skills
                </h2>
                <div className="space-y-6">
                  {renderField('bio', 'Bio', 'textarea', 'Tell us about yourself...', <AcademicCapIcon className="w-4 h-4" />)}
                  {renderField('skills', 'Skills', 'text', 'e.g., React, Node.js, MongoDB', <StarIcon className="w-4 h-4" />)}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-8">
              {/* Recent Activity */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
                  <ClockIcon className="w-5 h-5 text-blue-600" />
                  Recent Activity
                </h2>
                <div className="space-y-4">
                  {isLoadingActivities ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                      <span className="ml-2 text-sm text-gray-500">Loading activities...</span>
                    </div>
                  ) : recentActivity.length === 0 ? (
                    <div className="text-center py-8">
                      <ClockIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-sm text-gray-500">No recent activity</p>
                      <p className="text-xs text-gray-400 mt-1">Your activities will appear here</p>
                    </div>
                  ) : (
                    recentActivity.map((activity) => (
                      <div key={activity.id} className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                        <div className="text-2xl">{getActivityIcon(activity.type)}</div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {activity.description} {activity.relatedName ? `"${activity.relatedName}"` : ''}
                          </p>
                          <p className="text-xs text-gray-500">{formatActivityTime(activity.timestamp)}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Quick Stats */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Quick Stats</h2>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                    <span className="text-sm font-medium text-blue-900">Teams Joined</span>
                    <span className="text-lg font-bold text-blue-600">{activityStats.teamsJoined}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                    <span className="text-sm font-medium text-green-900">My Designs</span>
                    <span className="text-lg font-bold text-green-600">{activityStats.myDesigns}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                    <span className="text-sm font-medium text-purple-900">Collaborations</span>
                    <span className="text-lg font-bold text-purple-600">{activityStats.collaborations}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <AppFooter />
    </div>
  );
};

export default ProfilePage; 